/**
 * Content Service for Firebase
 * 
 * Handles bookmark and reading progress operations for all content types.
 * Note: CRUD operations are now handled by module-specific service files
 * (legacy per-type RTDB modules may still exist; unified rows live under `contents`).
 */

import { ref, set, get, remove } from "firebase/database";
import { db } from "../../auth/firebase";
import { pushActivity, ACTIVITY_TYPES } from "../../services/activityLogService";
import { logError } from "../../utils/errorHandler";

/**
 * Add a bookmark for a user
 * @param {string} userId - The user ID
 * @param {string} contentType - Bookmark bucket (`read`, `articles`, or legacy keys)
 * @param {string} itemId - The ID of the content item
 * @param {{ userEmail?: string, userName?: string, itemTitle?: string }} [meta] - Optional for activity log
 * @returns {Promise<void>}
 */
export const addBookmark = async (userId, contentType, itemId, meta = {}) => {
  try {
    const bookmarkRef = ref(db, `users/${userId}/bookmarks/${contentType}/${itemId}`);
    await set(bookmarkRef, {
      contentType,
      itemId,
      bookmarkedAt: new Date().toISOString(),
    });
    pushActivity({
      type: ACTIVITY_TYPES.BOOKMARK_ADDED,
      userId,
      userEmail: meta.userEmail,
      userName: meta.userName,
      contentType,
      contentId: itemId,
      title: meta.itemTitle,
    }).catch(() => {});
  } catch (error) {
    logError(error, "addBookmark");
    throw error;
  }
};

/**
 * Remove a bookmark for a user
 * @param {string} userId - The user ID
 * @param {string} contentType - Bookmark bucket (`read`, `articles`, or legacy keys)
 * @param {string} itemId - The ID of the content item
 * @param {{ userEmail?: string, userName?: string, itemTitle?: string }} [meta] - Optional for activity log
 * @returns {Promise<void>}
 */
export const removeBookmark = async (userId, contentType, itemId, meta = {}) => {
  try {
    const bookmarkRef = ref(db, `users/${userId}/bookmarks/${contentType}/${itemId}`);
    await remove(bookmarkRef);
    pushActivity({
      type: ACTIVITY_TYPES.BOOKMARK_REMOVED,
      userId,
      userEmail: meta.userEmail,
      userName: meta.userName,
      contentType,
      contentId: itemId,
      title: meta.itemTitle,
    }).catch(() => {});
  } catch (error) {
    logError(error, "removeBookmark");
    throw error;
  }
};

/**
 * Whether this user has saved the item in the given bookmark bucket.
 */
export async function isContentBookmarked(userId, contentType, itemId) {
  const snapshot = await get(ref(db, `users/${userId}/bookmarks/${contentType}/${itemId}`));
  return snapshot.exists();
}

/**
 * Get all bookmarks for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Object with bookmarks organized by content type
 */
export const getUserBookmarks = async (userId) => {
  try {
    const bookmarksRef = ref(db, `users/${userId}/bookmarks`);
    const snapshot = await get(bookmarksRef);
    
    if (!snapshot.exists()) {
      return {};
    }
    
    const bookmarks = {};
    snapshot.forEach((contentTypeSnapshot) => {
      const contentType = contentTypeSnapshot.key;
      bookmarks[contentType] = {};
      contentTypeSnapshot.forEach((itemSnapshot) => {
        bookmarks[contentType][itemSnapshot.key] = itemSnapshot.val();
      });
    });
    
    return bookmarks;
  } catch (error) {
    console.error("Error fetching bookmarks:", error);
    throw error;
  }
};

/**
 * Save reading progress for a user
 * @param {string} userId - The user ID
 * @param {string} contentType - Bookmark bucket (`read`, `articles`, or legacy keys)
 * @param {string} itemId - The ID of the content item
 * @param {number} progress - Reading progress percentage (0-100)
 * @returns {Promise<void>}
 */
export const saveReadingProgress = async (userId, contentType, itemId, progress) => {
  try {
    const progressRef = ref(db, `users/${userId}/readingProgress/${contentType}/${itemId}`);
    await set(progressRef, {
      contentType,
      itemId,
      progress: Math.min(100, Math.max(0, progress)),
      lastReadAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error saving reading progress:", error);
    throw error;
  }
};

/**
 * Get reading progress for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Object with reading progress organized by content type
 */
export const getUserReadingProgress = async (userId) => {
  try {
    const progressRef = ref(db, `users/${userId}/readingProgress`);
    const snapshot = await get(progressRef);
    
    if (!snapshot.exists()) {
      return {};
    }
    
    const progress = {};
    snapshot.forEach((contentTypeSnapshot) => {
      const contentType = contentTypeSnapshot.key;
      progress[contentType] = {};
      contentTypeSnapshot.forEach((itemSnapshot) => {
        progress[contentType][itemSnapshot.key] = itemSnapshot.val();
      });
    });
    
    return progress;
  } catch (error) {
    console.error("Error fetching reading progress:", error);
    throw error;
  }
};

